import { ORPCError } from "@orpc/client";
import { getVisitAudio, replaceVisitAudio } from "@/services/audio-uploads";
import {
  prepareOwnedAudioFile,
  uploadOwnedAudioFile,
} from "./owned-audio-upload";
import {
  acceptVisitAudioAttachment,
  beginVisitAudioAttachment,
  readVisitAudioAttachment,
  rejectVisitAudioAttachment,
  rememberVisitAudioUpload,
} from "./visit-audio-attachment-identity";

export const prepareVisitAudioAttachmentFile = async ({
  parentPublicId,
  file,
  signal,
}: {
  parentPublicId: string;
  file: Blob;
  signal?: AbortSignal;
}) => {
  const prepared = await prepareOwnedAudioFile(file, signal);
  const bytes = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(
      JSON.stringify([
        "visit-audio-attachment-v1",
        parentPublicId,
        prepared.input,
      ]),
    ),
  );
  signal?.throwIfAborted();
  const fingerprint = Array.from(new Uint8Array(bytes), (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");
  return { ...prepared, parentPublicId, fingerprint };
};
export type PreparedVisitAudioAttachment = Awaited<
  ReturnType<typeof prepareVisitAudioAttachmentFile>
>;
export const runVisitAudioAttachment = async ({
  account,
  prepared,
  key,
  userUid,
  signal,
  isCurrent,
  allowFresh,
  onProgress,
}: {
  account: string;
  prepared: PreparedVisitAudioAttachment;
  key: string;
  userUid: string;
  signal: AbortSignal;
  isCurrent: () => boolean;
  allowFresh: boolean;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}): Promise<{ public_id: string }> => {
  const check = () => {
    signal.throwIfAborted();
    if (!isCurrent())
      throw new DOMException("Audio attachment session changed", "AbortError");
  };
  check();
  let state = readVisitAudioAttachment(account, prepared.parentPublicId);
  if (!state || state.key !== key || state.fingerprint !== prepared.fingerprint)
    throw Error("Audio attachment changed. Keep the original retry identity.");
  if (state.kind === "accepted") return { public_id: state.parentPublicId };
  if (state.kind === "rejected")
    throw Error(
      "Audio attachment was rejected. Check Visits before explicitly abandoning it.",
    );
  if (!allowFresh && state.kind !== "accepting")
    throw Error(
      "Audio attachments are paused. Keep this retry identity and check Visits.",
    );
  const identity = { userUid, signal };
  if (state.kind === "intent" || state.kind === "upload") {
    let ready = state;
    await uploadOwnedAudioFile({
      file: prepared.file,
      input: prepared.input,
      identity: { ...identity, key: state.uploadKey },
      uploadId: state.kind === "upload" ? state.uploadId : undefined,
      onCreated: (id) => {
        if (ready.kind === "intent")
          ready = rememberVisitAudioUpload(account, ready, id);
        else if (ready.uploadId !== id)
          throw Error("Upload recovery identity changed");
      },
      isCurrent,
      onProgress,
    });
    check();
    if (ready.kind !== "upload") throw Error("Upload identity was not saved");
    const current = await getVisitAudio(state.parentPublicId, identity);
    check();
    state = beginVisitAudioAttachment(account, ready, current.version);
  }
  check();
  try {
    const result = await replaceVisitAudio(
      state.parentPublicId,
      { audioUploadId: state.uploadId, baseVersion: state.baseVersion },
      { ...identity, key: state.key },
    );
    check();
    acceptVisitAudioAttachment(account, state);
    return result;
  } catch (error) {
    check();
    if (error instanceof ORPCError && error.code === "CONFLICT")
      rejectVisitAudioAttachment(account, state);
    throw error;
  }
};
